/* Shared SEO landing template for premium event cruise pages.
   Layout (top to bottom):
   Hero · Highlights · Short intro · Packages · Local SEO · Reviews ·
   LONG SEO ARTICLE (3 subsections rendered as one flowing article, 1 inline image)
   · FAQ · Final CTA
*/
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Anchor, Crown, Users, Check, Star, ChevronRight, Phone, GlassWater, Clock, MapPin, Sparkles } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import EnquiryForm from "../components/EnquiryForm";
import ReserveDateButton from "../components/ReserveDateButton";
import DateAvailabilityChecker from "../components/DateAvailabilityChecker";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { MAGIC_PHOTOS, CONTACT } from "../data/cruiseData";
import { EVENT_LANDINGS } from "../data/eventLandings";

const API = process.env.REACT_APP_BACKEND_URL;

// Slugs that have an admin-editable package set in MongoDB (only slugs that
// render `cfg.packages.tiers` via this template; seoOnly slugs are skipped).
const SLUG_TO_CMS_TYPE = {
 "wedding-cruises-sydney-harbour": "wedding",
 "corporate-cruises-sydney-harbour": "corporate",
 "christmas-party-cruises-sydney": "christmas",
 "birthday-cruises-sydney-harbour": "birthday",
};

/* Merge live CMS tiers into the EventLanding `cfg.packages` shape, preserving
   layout-only fields (badge, cta, tier, priceUnit, highlight) from cfg while
   overwriting editable fields (price, inclusions, menu, sub, extras). */
function mergeCmsIntoCfg(cfg, cmsDoc) {
 if (!cfg?.packages?.tiers || !cmsDoc?.packages) return cfg;
 const cfgTiers = cfg.packages.tiers;
 const cmsTiers = cmsDoc.packages;
 const mergedTiers = cfgTiers.map((cfgTier, i) => {
  const cmsTier = cmsTiers[i];
  if (!cmsTier) return cfgTier;
  // Build "caption" line from CMS sub + extras (matches EventLanding's caption slot)
  const captionParts = [cmsTier.sub, cmsTier.extras].filter(Boolean);
  return {
   ...cfgTier,
   price: cmsTier.price || cfgTier.price,
   caption: captionParts.length ? captionParts.join(" · ") : cfgTier.caption,
   inclusions: cmsTier.inclusions && cmsTier.inclusions.length ? cmsTier.inclusions : cfgTier.inclusions,
   menu: cmsTier.menu && Object.keys(cmsTier.menu).length ? cmsTier.menu : cfgTier.menu,
   highlight: cmsTier.featured !== undefined ? cmsTier.featured : cfgTier.highlight,
  };
 });
 return { ...cfg, packages: { ...cfg.packages, tiers: mergedTiers } };
}

export default function EventLanding() {
 const { pathname } = useLocation();
 const slug = pathname.replace(/^\/cruises\//, "").replace(/\/$/, "");
 const staticCfg = EVENT_LANDINGS[slug];
 const [cmsDoc, setCmsDoc] = useState(null);

 useEffect(() => { if (staticCfg) document.title = `${staticCfg.title} | Magic Cruises`; }, [staticCfg]);

 // Pull live CMS prices/menu/inclusions for supported slugs.
 useEffect(() => {
  const cmsType = SLUG_TO_CMS_TYPE[slug];
  if (!cmsType) return;
  let cancelled = false;
  fetch(`${API}/api/cms/packages/${cmsType}`)
   .then((r) => r.ok ? r.json() : null)
   .then((doc) => { if (!cancelled && doc && doc.packages) setCmsDoc(doc); })
   .catch(() => { /* keep static fallback */ });
  return () => { cancelled = true; };
 }, [slug]);

 const cfg = useMemo(() => mergeCmsIntoCfg(staticCfg, cmsDoc), [staticCfg, cmsDoc]);

 if (!cfg) {
  return (
   <main className="min-h-[70vh] flex items-center justify-center text-white bg-[#06060C]">
    <div className="text-center"><h1 className="text-3xl font-black">Page not found</h1><Link to="/event-cruises" className="text-amber-300 mt-4 inline-block">Back to events</Link></div>
   </main>
  );
 }

 const t = cfg.theme;
 const accent = t.accent;
 const hot = t.hot;
 const dark1 = t.dark1;
 const dark2 = t.dark2;
 const isSeoOnly = !!cfg.seoOnly;
 const heroCtaHref = isSeoOnly ? "#enquire" : "#packages";
 const heroCtaLabel = isSeoOnly ? "Check availability" : "See packages";

 // Map cruise slug → deposit cruise type (for the "Reserve this date · $1,000 deposit" CTA).
 // Only enabled for Wedding, Corporate and Christmas. BYO has its own button on /byo-cruise-sydney.
 const DEPOSIT_TYPES = {
  "wedding-cruises-sydney-harbour": ["wedding", "Wedding Cruise"],
  "corporate-cruises-sydney-harbour": ["corporate", "Corporate Cruise"],
  "christmas-party-cruises-sydney": ["christmas", "Christmas Party Cruise"],
 };
 const [depositCruiseType, depositCruiseLabel] = DEPOSIT_TYPES[slug] || [null, null];

 return (
  <main className="text-white overflow-hidden" style={{ backgroundColor: dark1 }} data-testid={`event-landing-${slug}`}>
   <SEOHead title={cfg.title} description={cfg.metaDescription} canonical={`https://magiccruises.com.au/cruises/${slug}`} faqItems={cfg.faq} includeLocalBusiness={true} />

   {/* HERO */}
   <section className="relative overflow-hidden flex flex-col isolate" style={{ backgroundColor: dark1 }} data-testid={`${slug}-hero`}>
    <div className="relative w-full min-h-[82vh] flex items-center justify-center px-5 sm:px-8 lg:px-10 pt-28 pb-16 lg:py-28">
     <img src={cfg.hero.image} alt={`${cfg.h1} , Sydney Harbour cruise aboard MV Sydney Magic`} className="absolute inset-0 w-full h-full object-cover scale-110" style={{ opacity: cfg.hero.opacity ?? 0.55 }} fetchPriority="high" />
     <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${dark1}E6 0%, ${dark2}8C 30%, ${dark1}E6 100%)` }} />
     <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-65" style={{ background: `radial-gradient(ellipse at center top, ${accent}33 0%, transparent 60%)` }} />
     <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[120%] h-[55%] opacity-55" style={{ background: `radial-gradient(ellipse at center bottom, ${hot}33 0%, transparent 65%)` }} />
     <Starfield density={45} color={t.star} glow={`${t.star}80`} fixed={false} intensity={0.5} />

     <h1 className="sr-only">{cfg.h1}</h1>

     <div className="relative z-10 text-center max-w-5xl mx-auto w-full">
      <div className="inline-flex items-center gap-3 sm:gap-4 mb-5 lg:mb-7">
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: accent, opacity: 0.8 }} />
       <span className="text-white/95 text-[11px] sm:text-[13px] lg:text-sm uppercase tracking-[0.45em] font-bold">{cfg.hero.eyebrow}</span>
       <span className="block w-10 sm:w-14 h-px" style={{ backgroundColor: accent, opacity: 0.8 }} />
      </div>
      <h2 className="font-black tracking-tight leading-[0.9]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.75rem, 10vw, 8rem)", letterSpacing: "-0.02em" }}>
       <span className="block bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, #FFFFFF 0%, ${t.tint} 35%, ${accent} 70%, ${hot} 100%)`, filter: `drop-shadow(0 4px 28px ${accent}66)` }}>{cfg.hero.titleLine1}</span>
       <em className="block italic font-light mt-2 lg:mt-3 text-white/92" style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.8rem)" }}>{cfg.hero.titleLine2}</em>
      </h2>
      <p className="text-white/85 text-base sm:text-lg lg:text-xl mt-6 lg:mt-7 max-w-2xl mx-auto leading-[1.65]">{cfg.hero.tagline}</p>
      <div className="mt-9 lg:mt-12 flex flex-wrap items-center justify-center gap-3">
       <a href={heroCtaHref} className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${t.tint} 0%, ${accent} 100%)`, boxShadow: `0 20px 50px -10px ${accent}88` }} data-testid={`${slug}-hero-cta`}>
        {heroCtaLabel} <ArrowRight className="w-4 h-4" />
       </a>
       <a href={`tel:${CONTACT.phone1}`} className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-white/60 transition-all text-white">
        <Phone className="w-4 h-4" /> {CONTACT.phone1}
       </a>
      </div>
      <div className="mt-10 lg:mt-14 grid grid-cols-3 gap-4 lg:gap-10 max-w-3xl mx-auto">
       {cfg.heroFacts.map((c, i) => {
        const Icon = { Anchor, Clock, Users, MapPin, GlassWater, Crown, Sparkles }[c.icon] || Anchor;
        return (
         <div key={i} className="flex flex-col items-center gap-2 lg:gap-3 text-center">
          <span className="w-11 h-11 lg:w-14 lg:h-14 rounded-full flex items-center justify-center ring-1 backdrop-blur" style={{ backgroundColor: `${accent}1A`, borderColor: accent }}>
           <Icon className="w-4 h-4 lg:w-6 lg:h-6" style={{ color: accent }} strokeWidth={1.5} />
          </span>
          <div className="text-white text-[10px] sm:text-[11px] lg:text-sm uppercase tracking-[0.18em] lg:tracking-[0.22em] font-black leading-snug">
           {c.lines.map((line, j) => <div key={j}>{line}</div>)}
          </div>
         </div>
        );
       })}
      </div>
     </div>
    </div>
   </section>

   {/* HIGHLIGHTS STRIP */}
   <section className="relative border-y" style={{ background: `linear-gradient(90deg, ${dark1}, ${dark2}, ${dark1})`, borderColor: `${accent}26` }} data-testid={`${slug}-highlights`}>
    <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-14">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-x-10">
      {cfg.highlights.map((h, i) => {
       const Icon = { Crown, Users, Anchor, GlassWater, Sparkles, Clock, MapPin }[h.icon] || Crown;
       return (
        <div key={i} className="flex items-center gap-4 lg:border-r lg:pr-6 last:border-0" style={{ borderColor: `${accent}26` }}>
         <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 ring-1" style={{ backgroundColor: `${accent}1A`, borderColor: accent }}>
          <Icon className="w-6 h-6" style={{ color: accent }} strokeWidth={1.5} />
         </div>
         <div>
          <div className="font-black text-white text-sm tracking-[0.18em]">{h.label}</div>
          <div className="text-white/60 text-[11px] uppercase tracking-[0.22em] font-bold mt-1">{h.sub}</div>
         </div>
        </div>
       );
      })}
     </div>
    </div>
   </section>

   {/* SEO LANDING: inline enquiry form + live availability calendar (only on seoOnly pages) */}
  {isSeoOnly && (
   <section id="enquire" className="relative py-16 lg:py-20 overflow-hidden isolate border-y" style={{ background: `linear-gradient(180deg, ${dark1}, ${dark2}, ${dark1})`, borderColor: `${accent}22` }} data-testid={`${slug}-enquire-section`}>
    <Starfield density={18} color={t.star} glow={`${t.star}55`} fixed={false} intensity={0.35} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-12">
      <Eyebrow accent={accent}>Check availability · Enquire now</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-5 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.9rem, 4.2vw, 3.5rem)" }}>
       Plan your Sydney Harbour cruise. <em className="font-light italic" style={{ color: accent }}>Reply in 24 hours.</em>
      </h2>
      <p className="text-white/72 text-base lg:text-lg mt-4 font-light">Pick a date on the live availability calendar, then send a quick enquiry. Our team replies within 24 hours with a tailored quote for your Sydney Harbour cruise.</p>
     </div>
     <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
      <div data-testid={`${slug}-enquire-form-wrap`}>
       <EnquiryForm defaultEvent={cfg.title} sourcePage={`/cruises/${slug}`} />
      </div>
      <div data-testid={`${slug}-enquire-calendar-wrap`}>
       <AvailabilityCalendar />
      </div>
     </div>
    </div>
   </section>
  )}

  {/* SHORT INTRO (1 paragraph) */}
   <section className="relative py-20 lg:py-24 overflow-hidden isolate" style={{ background: `linear-gradient(180deg, ${dark1}, ${dark2}, ${dark1})` }}>
    <Starfield density={20} color={t.star} glow={`${t.star}60`} fixed={false} intensity={0.35} />
    <div className="relative z-10 mx-auto max-w-4xl px-5 lg:px-10 text-center">
     <Eyebrow accent={accent}>{cfg.intro.eyebrow}</Eyebrow>
     <h2 className="font-black tracking-tight text-white mt-6 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 4rem)" }}>
      {cfg.intro.heading} <em className="font-light italic" style={{ color: accent }}>{cfg.intro.headingEm}</em>
     </h2>
     <p className="text-white/82 text-base lg:text-lg leading-[1.85] mt-6 max-w-3xl mx-auto font-light">{cfg.intro.body[0]}</p>
    </div>
   </section>

   {/* PACKAGES (hidden on seoOnly pages) */}
   {!isSeoOnly && cfg.packages && (
   <section id="packages" className="relative py-24 lg:py-32 overflow-hidden isolate border-y" style={{ background: `linear-gradient(180deg, ${dark1}, ${dark2}, ${dark1})`, borderColor: `${accent}22` }} data-testid={`${slug}-packages`}>
    <Starfield density={25} color={t.star} glow={`${t.star}55`} fixed={false} intensity={0.4} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-14">
      <Eyebrow accent={accent}>{cfg.packages.eyebrow}</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 4rem)" }}>
       {cfg.packages.heading} <em className="italic font-light" style={{ color: accent }}>{cfg.packages.headingEm}</em>
      </h2>
      <p className="text-white/65 text-base lg:text-lg mt-5 font-light">{cfg.packages.intro}</p>
     </div>

     {/* Prominent reserve-date CTA banner , sits directly above the tier cards */}
     {depositCruiseType && (
      <div className="relative rounded-3xl ring-2 mb-10 lg:mb-14 px-6 sm:px-10 py-7 sm:py-9 backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-700"
       style={{ background: `linear-gradient(135deg, ${accent}26 0%, ${dark2}cc 60%, ${dark1}aa 100%)`, borderColor: `${accent}88`, boxShadow: `0 25px 70px -15px ${accent}55, inset 0 0 0 1px ${accent}22` }}
       data-testid={`${slug}-deposit-banner`}>
       <div className="grid lg:grid-cols-2 gap-7 lg:gap-10 items-start">
        {/* LEFT: Copy */}
        <div>
         <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-black mb-4" style={{ backgroundColor: accent, color: "#0A0A0A" }}>
          <Sparkles className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Secure Your Date
         </div>
         <h3 className="text-white text-2xl sm:text-3xl font-black tracking-tight leading-tight">
          Lock in your {depositCruiseLabel.toLowerCase()} date with a <span style={{ color: accent }}>$1,000 deposit</span>
         </h3>
         <p className="text-white/70 text-sm sm:text-base mt-3 leading-relaxed">
          Pay your <strong className="text-white">$1,000 holding deposit</strong> today and we'll lock that date in. It applies as credit toward your final cruise total. Balance due 14 days before the cruise.
         </p>
        </div>

        {/* RIGHT: Date checker → reserve flow */}
        <div className="w-full">
         <DateAvailabilityChecker cruiseType={depositCruiseType} cruiseLabel={depositCruiseLabel} accent="bg-yellow-400 hover:bg-yellow-300 !text-slate-900 shadow-yellow-400/40" testid={`${slug}-date-checker`} />
        </div>
       </div>
      </div>
     )}

     <div className={`grid lg:grid-cols-${cfg.packages.tiers.length === 3 ? 3 : 2} gap-7 lg:gap-9`}>
      {cfg.packages.tiers.map((p, i) => (
       <div key={i} className={`relative rounded-3xl ring-1 p-8 lg:p-10 backdrop-blur-md ${p.highlight ? "lg:scale-[1.02] z-10" : ""}`} style={{ background: p.highlight ? `linear-gradient(135deg, ${accent}26, ${dark2}cc 60%)` : "rgba(255,255,255,0.04)", borderColor: p.highlight ? `${accent}66` : "rgba(255,255,255,0.12)", boxShadow: p.highlight ? `0 30px 80px -20px ${accent}66` : "0 30px 80px -20px rgba(0,0,0,0.4)" }}>
        <div className="flex items-center justify-between mb-6">
         <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-black ${p.highlight ? "" : "ring-1 ring-white/25 bg-white/10 text-white"}`} style={p.highlight ? { backgroundColor: accent, color: "#0A0A0A" } : {}}>{p.badge}</span>
         <Crown className="w-6 h-6" style={{ color: p.highlight ? accent : "rgba(255,255,255,0.5)" }} strokeWidth={1.5} />
        </div>
        <div className="text-white/65 text-[11px] uppercase tracking-[0.32em] font-bold mb-2">{p.tier}</div>
        <h3 className="text-white text-2xl lg:text-3xl font-black mb-4">{p.subtitle}</h3>
        <div className="flex items-baseline gap-3 mb-1">
         <span className="text-5xl lg:text-6xl font-black text-white tabular-nums">{p.price}</span>
         {p.priceUnit !== "" && <span className="text-white/55 text-sm font-bold">{p.priceUnit || "per person"}</span>}
        </div>
        <div className="text-white/55 text-xs uppercase tracking-widest font-bold mb-6">{p.caption}</div>
        <ul className="space-y-3 my-6 border-t border-white/10 pt-6">
         {p.inclusions.map((inc, j) => (
          <li key={j} className="flex items-start gap-3 text-white/85 text-sm">
           <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} strokeWidth={2.5} /><span>{inc}</span>
          </li>
         ))}
        </ul>
        {p.menu && (
         <div className="border-t border-white/10 pt-5 mb-5 space-y-4">
          {Object.entries(p.menu).map(([section, items]) => (
           <div key={section} data-testid={`${slug}-pkg-menu-${i}-${section.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-2.5" style={{ color: accent }}>{section}</div>
            <ul className="space-y-2">
             {items.map((item, k) => (
              <li key={k} className="text-white/70 text-[13px] leading-snug pl-3 border-l-2" style={{ borderColor: `${accent}55` }}>{item}</li>
             ))}
            </ul>
           </div>
          ))}
         </div>
        )}
        <Link to="/contact" className={`mt-2 group flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase transition-all ${p.highlight ? "text-[#0A0A0A]" : "text-white ring-1 ring-white/25 hover:text-white"}`} style={p.highlight ? { background: `linear-gradient(135deg, ${t.tint} 0%, ${accent} 100%)`, boxShadow: `0 15px 40px -10px ${accent}88` } : {}}>
         {p.cta} <ArrowRight className="w-4 h-4" />
        </Link>
       </div>
      ))}
     </div>
    </div>
   </section>
  )}

   {/* LOCAL SEO PANEL , only when defined (skipped on seoOnly minimal pages) */}
   {cfg.localSEO && (
   <section className="relative py-20 lg:py-28 overflow-hidden isolate" style={{ background: dark1 }} data-testid={`${slug}-local-seo`}>
    <Starfield density={16} color={t.star} glow={`${t.star}50`} fixed={false} intensity={0.3} />
    <div className="relative z-10 mx-auto max-w-6xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-12">
      <Eyebrow accent={accent}>{cfg.localSEO.eyebrow}</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.95]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
       {cfg.localSEO.heading} <em className="italic font-light" style={{ color: accent }}>{cfg.localSEO.headingEm}</em>
      </h2>
      <p className="text-white/72 text-base lg:text-lg mt-5 leading-[1.7] font-light">{cfg.localSEO.body}</p>
     </div>
     <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {cfg.localSEO.spots.map((s, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6">
        <MapPin className="w-5 h-5 mb-3" style={{ color: accent }} strokeWidth={1.5} />
        <h4 className="font-black text-white text-lg mb-1.5">{s.name}</h4>
        <p className="text-white/65 text-sm leading-[1.65]">{s.note}</p>
       </div>
      ))}
     </div>
    </div>
   </section>
   )}

   {/* REVIEWS , only when defined (skipped on seoOnly minimal pages) */}
   {cfg.reviews && (
   <section className="relative py-20 lg:py-28 border-y overflow-hidden isolate" style={{ background: dark2, borderColor: `${accent}22` }} data-testid={`${slug}-reviews`}>
    <Starfield density={20} color={t.star} glow={`${t.star}50`} fixed={false} intensity={0.35} />
    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-2xl mx-auto mb-14">
      <Eyebrow accent={accent}>Guest reviews · 4.9 / 5</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
       {cfg.reviews.heading} <em className="italic font-light" style={{ color: accent }}>{cfg.reviews.headingEm}</em>
      </h2>
     </div>
     <div className="grid md:grid-cols-3 gap-6">
      {cfg.reviews.items.map((r, i) => (
       <div key={i} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-6 lg:p-7">
        <div className="flex items-center gap-1 mb-4">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-current" style={{ color: accent }} />)}</div>
        <p className="text-white/85 text-base leading-[1.7] italic mb-5">&ldquo;{r.body}&rdquo;</p>
        <div className="text-white font-black text-sm">{r.name}</div>
        <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold">{r.role}</div>
       </div>
      ))}
     </div>
    </div>
   </section>
   )}

   {/* LONG SEO ARTICLE (~1500 words), sits lower, magazine style flow, 1 inline image */}
   <section className="relative py-24 lg:py-32 overflow-hidden isolate" style={{ background: dark1 }} data-testid={`${slug}-seo-article`}>
    <Starfield density={14} color={t.star} glow={`${t.star}45`} fixed={false} intensity={0.28} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center mb-12">
      <Eyebrow accent={accent}>{cfg.article.eyebrow}</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-6 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
       {cfg.article.heading} <em className="italic font-light" style={{ color: accent }}>{cfg.article.headingEm}</em>
      </h2>
     </div>

     <article className="prose-content space-y-12">
      {cfg.whyBlocks.map((block, i) => (
       <div key={i} className="space-y-5">
        <h3 className="font-black tracking-tight text-white leading-[1.1]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: accent }}>
         {block.heading}
        </h3>
        {block.body.map((p, j) => (
         <p key={j} className="text-white/82 text-base lg:text-lg leading-[1.85] font-light">{renderInline(p, accent)}</p>
        ))}
        {/* Inline image only in the middle block (block index 1) */}
        {i === 1 && cfg.article.inlineImage && (
         <figure className="my-10 -mx-5 sm:-mx-0">
          <div className="relative aspect-[16/9] rounded-2xl overflow-hidden ring-1" style={{ borderColor: `${accent}33` }}>
           <img src={cfg.article.inlineImage.src} alt={cfg.article.inlineImage.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
           <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>
          {cfg.article.inlineImage.caption && (
           <figcaption className="text-white/55 text-xs uppercase tracking-[0.22em] font-bold text-center mt-3">{cfg.article.inlineImage.caption}</figcaption>
          )}
         </figure>
        )}
        {block.checklist && (
         <ul className="grid sm:grid-cols-2 gap-2.5 pt-3">
          {block.checklist.map((c, j) => (
           <li key={j} className="flex items-start gap-2.5 text-white/85 text-sm">
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: accent }} strokeWidth={2.5} /><span>{c}</span>
           </li>
          ))}
         </ul>
        )}
       </div>
      ))}
     </article>
    </div>
   </section>

   {/* FAQ */}
   <section className="relative py-20 lg:py-24 overflow-hidden isolate border-t" style={{ background: dark2, borderColor: `${accent}22` }} data-testid={`${slug}-faq`}>
    <Starfield density={14} color={t.star} glow={`${t.star}48`} fixed={false} intensity={0.28} />
    <div className="relative z-10 mx-auto max-w-3xl px-5 lg:px-10">
     <div className="text-center mb-10">
      <Eyebrow accent={accent}>Questions, answered</Eyebrow>
      <h2 className="font-black tracking-tight text-white mt-5 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.9rem, 4vw, 3.25rem)" }}>
       {cfg.faqHeading}
      </h2>
     </div>
     <div className="space-y-3">
      {cfg.faq.map((f, i) => (
       <details key={i} className="group rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-5 lg:p-6 cursor-pointer">
        <summary className="flex items-center justify-between gap-4 font-black text-white text-base lg:text-lg list-none">
         <span>{f.q}</span>
         <ChevronRight className="w-5 h-5 shrink-0 transition-transform group-open:rotate-90" style={{ color: accent }} />
        </summary>
        <p className="text-white/72 text-base leading-[1.78] mt-4">{f.a}</p>
       </details>
      ))}
     </div>
    </div>
   </section>

   {/* FINAL CTA */}
   <section id="enquiry" className="relative overflow-hidden isolate" data-testid={`${slug}-final-cta`}>
    <img src={cfg.finalCta.image} alt={`Book your ${cfg.h1} , Sydney Harbour cruise on MV Sydney Magic`} className="absolute inset-0 w-full h-full object-cover scale-110" />
    <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${dark1}F0, ${dark2}BF, ${dark2}66)` }} />
    <Starfield density={30} color={t.star} glow={`${t.star}66`} fixed={false} intensity={0.4} />
    <div className="relative z-10 mx-auto max-w-5xl px-5 lg:px-10 py-24 lg:py-32 text-center">
     <Eyebrow accent={accent}>{cfg.finalCta.eyebrow}</Eyebrow>
     <h2 className="font-black tracking-tight text-white mt-7 leading-[0.92]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}>
      {cfg.finalCta.heading} <em className="italic font-light" style={{ color: accent }}>{cfg.finalCta.headingEm}</em>
     </h2>
     <p className="text-white/82 text-base lg:text-lg leading-[1.7] mt-6 max-w-2xl mx-auto">{cfg.finalCta.body}</p>
     <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
      <Link to="/contact" className="group inline-flex items-center gap-2.5 px-8 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${t.tint} 0%, ${accent} 100%)`, boxShadow: `0 20px 50px -10px ${accent}88` }} data-testid={`${slug}-final-enquiry`}>
       Send an enquiry <ArrowRight className="w-4 h-4" />
      </Link>
      <a href={`tel:${CONTACT.phone1}`} className="inline-flex items-center gap-2.5 text-white px-6 py-4 rounded-full font-black text-sm tracking-[0.18em] uppercase border border-white/30 hover:border-white/60 transition-all">
       <Phone className="w-4 h-4" /> {CONTACT.phone1}
      </a>
     </div>
    </div>
   </section>
  </main>
 );
}

function Eyebrow({ children, accent }) {
 return (
  <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] font-black">
   <span className="block w-6 h-px" style={{ backgroundColor: `${accent}99` }} />
   <span style={{ color: accent }}>{children}</span>
   <span className="block w-6 h-px" style={{ backgroundColor: `${accent}99` }} />
  </div>
 );
}

// Parse [text](path) Markdown-style internal links into React Link components.
// External links (starting with http) become <a> with target="_blank".
function renderInline(text, accent) {
 if (typeof text !== "string") return text;
 const parts = [];
 const re = /\[([^\]]+)\]\(([^)]+)\)/g;
 let last = 0;
 let m;
 let key = 0;
 while ((m = re.exec(text)) !== null) {
  if (m.index > last) parts.push(text.slice(last, m.index));
  const label = m[1];
  const href = m[2];
  const isExternal = /^https?:/i.test(href);
  if (isExternal) {
   parts.push(
    <a key={`l-${key++}`} href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 decoration-2 hover:opacity-90 transition" style={{ color: accent, textDecorationColor: `${accent}66` }}>{label}</a>
   );
  } else {
   parts.push(
    <Link key={`l-${key++}`} to={href} className="underline underline-offset-4 decoration-2 hover:opacity-90 transition" style={{ color: accent, textDecorationColor: `${accent}66` }}>{label}</Link>
   );
  }
  last = m.index + m[0].length;
 }
 if (last < text.length) parts.push(text.slice(last));
 return parts.length ? parts : text;
}
